import { useEffect } from 'react';
import { useFieldArray, UseFormReturn } from 'react-hook-form';
import TaperForm from './TaperForm/TaperForm';
import { CareMapprTextarea } from '../../../Inputs/textarea';

interface TaperInstructionProps {
  labelOrientation?: string;
  form: UseFormReturn<any>;
  furtherInform?: React.ReactNode;
  basePath?: string;
}

const TaperInstruction = ({
  labelOrientation = 'column',
  form,
  furtherInform = <></>,
  basePath = '',
}: TaperInstructionProps) => {
  const { register, watch, control, setValue } = form;

  const getPath = (p: string) => `${basePath}${p}`;

  const { fields: taperSchedulFields, append } = useFieldArray({
    control,
    name: getPath('regimen.taper.taperSchedule'),
  });

  const isDoseScheduleWatch = watch(getPath('regimen.isDoseSchedule'));
  const isPrnWatch = watch(getPath('regimen.isPrn'));
  const isTaperWatch = watch(getPath('regimen.isTaper'));

  const isBreakfastWatch = watch(getPath('regimen.doseSchedule.isBreakfast'));
  const isLunchWatch = watch(getPath('regimen.doseSchedule.isLunch'));
  const isDinnerWatch = watch(getPath('regimen.doseSchedule.isDinner'));
  const isBedWatch = watch(getPath('regimen.doseSchedule.isBed'));

  const renderAutoResizeField = (
    name: string,
    controlId: string,
    placeholder: string,
    options: {
      rows?: number;
      inputClassName?: string;
      disabled?: boolean;
    } = {},
  ) => {
    const {
      rows = 1,
      inputClassName = 'med-input',
      disabled = false,
    } = options;
    return (
      <CareMapprTextarea
        autoResize
        rows={rows}
        controlId={controlId}
        placeholder={placeholder}
        inputClassName={inputClassName}
        disabled={disabled}
        value={String(watch(name) || '')}
        onChange={(e) => {
          setValue(name, e.target.value, { shouldDirty: true });
        }}
      />
    );
  };

  useEffect(() => {
    if (isTaperWatch) {
      if (
        !Array.isArray(taperSchedulFields) ||
        taperSchedulFields.length === 0
      ) {
        append({});
        // Ensure at least one taper header exists so the default row is visible
        setValue(getPath('regimen.taper.taperHeaders'), [{ title: '' }]);
      }
    }
  }, [isTaperWatch]);

  const clearDoseFields = () => {
    setValue(getPath('regimen.isDoseSchedule'), false);
  };

  const clearPrnFields = () => {
    setValue(getPath('regimen.isPrn'), false);
    setValue(getPath('regimen.prn.prnNote'), '');
  };

  const clearTaperFields = () => {
    setValue(getPath('regimen.isTaper'), false);
  };

  const handleAddTaperSchedule = () => {
    append({});
  };

  return (
    <div className="use-description-container">
      {labelOrientation === 'column' ? (
        <div className="use-description-options-container">
          <div style={{ marginRight: '15px', marginLeft: '15px' }}>
            How should patient take this?
          </div>
          <div className="form-check">
            <label htmlFor="scheduleRadio">
              <input
                className="form-check-input"
                type="radio"
                id="scheduleRadio"
                {...register(getPath('regimen.isDoseSchedule'), {
                  onChange: (event) => {
                    const value = event.target.checked;
                    if (value) {
                      setValue(getPath('regimen.isDoseSchedule'), true);
                      clearPrnFields();
                      clearTaperFields();
                    }
                  },
                })}
                checked={isDoseScheduleWatch}
              />
              <span className="form-check-label">Schedule</span>
            </label>
          </div>
          <div className="form-check">
            <label htmlFor="prnRadio">
              <input
                className="form-check-input"
                id="prnRadio"
                type="radio"
                {...register(getPath('regimen.isPrn'), {
                  onChange: (event) => {
                    const value = event.target.checked;
                    if (value) {
                      clearDoseFields();
                      setValue(getPath('regimen.isPrn'), true);
                      clearTaperFields();
                    }
                  },
                })}
                checked={isPrnWatch}
              />
              <span className="form-check-label">PRN/other</span>
            </label>
          </div>
          <div className="form-check">
            <label htmlFor="taperRadio">
              <input
                className="form-check-input"
                id="taperRadio"
                type="radio"
                {...register(getPath('regimen.isTaper'), {
                  onChange: (event) => {
                    const value = event.target.checked;
                    if (value) {
                      clearDoseFields();
                      clearPrnFields();
                      setValue(getPath('regimen.isTaper'), true);
                    }
                  },
                })}
                checked={isTaperWatch}
              />
              <span className="form-check-label">Taper</span>
            </label>
          </div>
        </div>
      ) : (
        <div className="row">
          <div className="col-6">
            <div style={{ marginRight: '15px', marginLeft: '15px' }}>
              How should patient take this?
            </div>
          </div>
          <div className="col-6 d-flex flex-wrap gap-1">
            <div className="form-check">
              <input
                className="form-check-input"
                type="radio"
                {...register(getPath('regimen.isDoseSchedule'), {
                  onChange: (event) => {
                    const value = event.target.checked;
                    if (value) {
                      setValue(getPath('regimen.isDoseSchedule'), true);
                      clearPrnFields();
                      clearTaperFields();
                    }
                  },
                })}
                checked={isDoseScheduleWatch}
              />
              <label className="form-check-label">Schedule</label>
            </div>
            <div className="form-check">
              <input
                className="form-check-input"
                type="radio"
                {...register(getPath('regimen.isPrn'), {
                  onChange: (event) => {
                    const value = event.target.checked;
                    if (value) {
                      clearDoseFields();
                      setValue(getPath('regimen.isPrn'), true);
                      clearTaperFields();
                    }
                  },
                })}
                checked={isPrnWatch}
              />
              <label className="form-check-label">PRN/other</label>
            </div>
            <div className="form-check">
              <input
                className="form-check-input"
                type="radio"
                {...register(getPath('regimen.isTaper'), {
                  onChange: (event) => {
                    const value = event.target.checked;
                    if (value) {
                      clearDoseFields();
                      clearPrnFields();
                      setValue(getPath('regimen.isTaper'), true);
                    }
                  },
                })}
                checked={isTaperWatch}
              />
              <label className="form-check-label">Taper</label>
            </div>
          </div>
        </div>
      )}
      {isDoseScheduleWatch && (
        <>
          <div style={{ marginLeft: '15px', marginRight: '15px' }}>
            <div className="schedule-container">
              <input
                className="form-check-input form-check-input-border"
                type="checkbox"
                {...register(getPath('regimen.doseSchedule.isBreakfast'))}
              />
              <span>Breakfast 7-9 AM</span>
              {renderAutoResizeField(
                getPath('regimen.doseSchedule.breakfastNote'),
                'breakfast-note',
                'Start typing...',
                {
                  inputClassName: `med-input ${isBreakfastWatch ? '' : 'med-input-not-checked'}`,
                  disabled: !isBreakfastWatch,
                },
              )}
            </div>
            <div className="schedule-container">
              <input
                className="form-check-input form-check-input-border"
                type="checkbox"
                {...register(getPath('regimen.doseSchedule.isLunch'))}
              />
              <span>Lunch 11-1 PM</span>
              {renderAutoResizeField(
                getPath('regimen.doseSchedule.lunchNote'),
                'lunch-note',
                'Start typing...',
                {
                  inputClassName: `med-input ${isLunchWatch ? '' : 'med-input-not-checked'}`,
                  disabled: !isLunchWatch,
                },
              )}
            </div>
            <div className="schedule-container">
              <input
                className="form-check-input form-check-input-border"
                type="checkbox"
                {...register(getPath('regimen.doseSchedule.isDinner'))}
              />
              <span>Dinner 4-6PM</span>
              {renderAutoResizeField(
                getPath('regimen.doseSchedule.dinnerNote'),
                'dinner-note',
                'Start typing...',
                {
                  inputClassName: `med-input ${isDinnerWatch ? '' : 'med-input-not-checked'}`,
                  disabled: !isDinnerWatch,
                },
              )}
            </div>
            <div className="schedule-container">
              <input
                className="form-check-input form-check-input-border"
                type="checkbox"
                {...register(getPath('regimen.doseSchedule.isBed'))}
              />
              <span>Bedtime 8-10PM</span>
              {renderAutoResizeField(
                getPath('regimen.doseSchedule.bedNote'),
                'bed-note',
                'Start typing...',
                {
                  inputClassName: `med-input ${isBedWatch ? '' : 'med-input-not-checked'}`,
                  disabled: !isBedWatch,
                },
              )}
            </div>
          </div>
        </>
      )}
      {isPrnWatch && (
        <>
          <div
            className="filterItem"
            style={{ marginLeft: '15px', marginRight: '15px' }}
          >
            <label className="txtDropdownLabel">Describe further</label>
            <div>
              {renderAutoResizeField(
                getPath('regimen.prn.prnNote'),
                'prn-note',
                'Start typing...',
                { rows: 6 },
              )}
            </div>
          </div>
        </>
      )}
      {isTaperWatch && (
        <TaperForm
          taperFields={taperSchedulFields}
          formWatch={watch}
          setFormValue={setValue}
          control={control}
          path={getPath}
          handleAddTaperSchedule={handleAddTaperSchedule}
        />
      )}
      {furtherInform}
    </div>
  );
};

export default TaperInstruction;
